var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import { Modal, ModalHeader } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useProfile } from '../../../hooks/use-profile';
import { accountApi } from '../../../api/common/account';
import { b2cAccountApi } from '../../../api/b2c/account';
import { addressesApi } from '../../../api/common/addresses';
import { buildAddressFromForm, isSameAddress, mapConvertToProValuesToRequestModel, redirectToB2bHomePage, } from '../../../utils/create-pro-modal';
import { mapPersonalInfoToOtpRequestModel, mapPersonalInfoToVerifyOtpRequestModel, } from '../../../utils/form';
import { getMaskedPhoneNumber, updateCurrentDefaultAdrFields, } from '../../../utils/general';
import { setOverlayEvent } from '../../../utils/analytics';
import { InfoModalContent } from './steps/info-modal-content';
import { VerifyMethodModalContent } from './steps/verify-method-modal-content';
import { VerifyCodeModalContent } from './steps/verify-code-modal-content';
import { CompanyDetailsModalContent, } from './steps/company-details-modal-content';
import { setCancelEvent } from '../../../utils/convert-to-pro-analytics';
var stepTitlesMap = {
    'confirm conversion': 'Convert to a Pro Xtra Account',
    'send code': 'Verify Your Identity',
    'confirm code': 'Verify Your Identity',
    'convert form': 'Provide Your Company Details',
};
export var ConvertToProModal = function (_a) {
    var _b;
    var open = _a.open, onClose = _a.onClose;
    var _c = useState('confirm conversion'), currentStage = _c[0], setCurrentStage = _c[1];
    var _d = useState(''), verificationCodeReceiver = _d[0], setVerificationCodeReceiver = _d[1];
    var isConsumerApp = useContext(ExperienceContext).isConsumerApp;
    var currentOtpResponse = useRef(null);
    var defaultAddress = useRef(null);
    var _e = useProfile(), customerData = _e.customerData, updatePersonalDetails = _e.updatePersonalDetails;
    var _f = customerData || {}, emailId = _f.emailId, phones = _f.phones, response = _f.response;
    var encryptVerifiedPhoneToken = (_b = response === null || response === void 0 ? void 0 : response.account) === null || _b === void 0 ? void 0 : _b.encryptVerifiedPhoneToken;
    var token = useRef('');
    // we can safely assert the data type here because this modal is only opened for b2c users
    var _g = useMemo(function () {
        return {
            verifiedPhone: phones === null || phones === void 0 ? void 0 : phones.find(function (phone) { return (phone === null || phone === void 0 ? void 0 : phone.phoneVerified) === 'Y'; }),
            primaryPhone: phones === null || phones === void 0 ? void 0 : phones.find(function (phone) { return (phone === null || phone === void 0 ? void 0 : phone.primaryFlag) === 'Y'; }),
        };
    }, [phones]), verifiedPhone = _g.verifiedPhone, primaryPhone = _g.primaryPhone;
    var maskedPhone = getMaskedPhoneNumber(verifiedPhone === null || verifiedPhone === void 0 ? void 0 : verifiedPhone.number);
    useEffect(function () {
        addressesApi
            .getDefaultAddress()
            .then(function (data) {
            defaultAddress.current = data || null;
        })
            .catch(function (err) {
            // swallow non api error
            if (err.message !== 'getDefaultAddress: no default address') {
                throw err;
            }
        });
    }, []);
    var onSendCode = function (verificationMethod) {
        if (!verificationMethod || !emailId || !encryptVerifiedPhoneToken) {
            return Promise.reject(new Error('No verification method was set, could not retreive email address, or could not retreive phone'));
        }
        var otpRequest = mapPersonalInfoToOtpRequestModel(emailId, encryptVerifiedPhoneToken, verificationMethod);
        return b2cAccountApi
            .getEmailOtp(otpRequest)
            .then(function (_a) {
            var data = _a.data;
            currentOtpResponse.current = data;
            setVerificationCodeReceiver(emailId);
            setCurrentStage('confirm code');
        })
            .catch(function (err) {
            currentOtpResponse.current = null;
            setVerificationCodeReceiver('');
            throw err;
        });
    };
    var onConfirmedCode = function (code) {
        if (!currentOtpResponse.current || !response) {
            return Promise.reject(new Error('Could not retrieve correct details'));
        }
        var requestModel = mapPersonalInfoToVerifyOtpRequestModel(currentOtpResponse.current, code, response);
        return b2cAccountApi.verifyProEmailOtp(requestModel).then(function (_a) {
            var data = _a.data;
            token.current = data.identity.token;
            setCurrentStage('convert form');
        });
    };
    var updateAuthId = function (authId) {
        if (currentOtpResponse.current) {
            currentOtpResponse.current.authId = authId;
        }
    };
    var onSubmit = function (values) {
        return new Promise(function (resolve, reject) {
            var newAddress = buildAddressFromForm(values);
            if (defaultAddress.current &&
                !isSameAddress(defaultAddress.current, newAddress)) {
                var currentDefaultAddress_1 = defaultAddress.current;
                // need to update default address if it already exists
                addressesApi
                    .addAddress(__assign(__assign({}, newAddress), { isDefault: false }))
                    .then(function (_a) {
                    var data = _a.data;
                    return addressesApi.editAddress(__assign(__assign({}, data.addresses.address[0]), { isDefault: true }), __assign({}, updateCurrentDefaultAdrFields(data.addresses.address[0], currentDefaultAddress_1)));
                })
                    .then(function (_a) {
                    var data = _a.data;
                    // if something goes wrong in the promise chain the customer may try again
                    // so we do not want to keep adding new addresses because it is always a different address
                    defaultAddress.current =
                        data.addresses.address.find(function (addr) { return addr.isDefault; }) || null;
                    resolve();
                })
                    .catch(reject);
            }
            else {
                resolve();
            }
        })
            .then(function () {
            var requestData = mapConvertToProValuesToRequestModel(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            customerData, __assign(__assign({}, values), { token: token.current }), !!defaultAddress.current);
            return updatePersonalDetails(requestData);
        })
            .then(function () {
            return accountApi
                .updateToPro()
                .then(function () {
                onUpgradeComplete(values.zipCode);
            })
                .catch(function (error) {
                // Handling 302 status code response
                if (error && typeof error.response === 'undefined') {
                    onUpgradeComplete(values.zipCode);
                }
                else {
                    throw error;
                }
            });
        });
    };
    var nativeAppEvent = function (zip) {
        var _a;
        (_a = window.LIFE_CYCLE_EVENT_BUS) === null || _a === void 0 ? void 0 : _a.trigger('NATIVE_APP.convertToPro', {
            zipCode: zip,
        });
    };
    var onUpgradeComplete = function (zip) {
        if (isConsumerApp) {
            nativeAppEvent(zip);
        }
        onClose();
        setOverlayEvent('pro xtra account created', {
            user: {
                customerType: 'pro',
            },
        });
        var params = new URLSearchParams(window.location.search);
        var redirect = params.get('redirect');
        if (redirect) {
            // plcc upgrade flow
            try {
                var url = new URL(decodeURIComponent(redirect), window.location.origin);
                // remove protocal and host from redirect url to prevent xss
                window.location.assign(url.pathname + url.search);
                return;
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error('error parsing redirect url:', err);
            }
        }
        redirectToB2bHomePage();
    };
    return (React.createElement(Modal, { open: open, onClose: onClose, size: "sm" },
        React.createElement(ModalHeader, { onClose: function () {
                setCancelEvent("convert to pro xtra overlay: cancel");
                onClose();
            } }, stepTitlesMap[currentStage]),
        currentStage === 'confirm conversion' && (React.createElement(InfoModalContent, { onCancel: onClose, onConfirm: function () { return setCurrentStage('send code'); } })),
        currentStage === 'send code' && (React.createElement(VerifyMethodModalContent, { onCancel: onClose, onConfirm: onSendCode, emailId: emailId, maskedPhone: maskedPhone })),
        currentStage === 'confirm code' && (React.createElement(VerifyCodeModalContent, { onCancel: onClose, verificationCodeReceiver: verificationCodeReceiver, onConfirm: onConfirmedCode, onSendCode: onSendCode, verifiedPhone: verifiedPhone, updateAuthId: updateAuthId })),
        currentStage === 'convert form' && (React.createElement(CompanyDetailsModalContent, { onSubmit: onSubmit, defaultFirstName: customerData === null || customerData === void 0 ? void 0 : customerData.firstName, defaultLastName: customerData === null || customerData === void 0 ? void 0 : customerData.lastName, defaultPhone: (primaryPhone === null || primaryPhone === void 0 ? void 0 : primaryPhone.number) || '' }))));
};
